import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styled from "styled-components";

import "../../styles/page-titles.css";
import PermissionGate from "../../services/PermissionGate";
import { SidebarContext } from "../../AppRoutes";
import { supportChatApi } from "../../services/supportChatApi";
import { MdShield } from "react-icons/md";

const base = "/api/v1/support-chat";
// Precisa bater com CONSENT_VERSION em backend-pedeja/endpoints/supportObservation.js -
// o backend rejeita liveAllowed:true sem essa versão exata.
const LIVE_CONSENT_VERSION = 2;

const PageContainer = styled.div`
    display: flex;
    margin-left: ${({ $sidebar }) => ($sidebar ? "250px" : "100px")};
    min-height: 100vh;
    transition: margin-left 200ms ease;
    background: #ffffff;

    @media (max-width: 880px) {
        margin-left: 0;
    }
`;

const ContentWrapper = styled.div`
    width: 100%;
    max-width: 640px;
    padding: 24px;

    @media (max-width: 768px) {
        padding: 16px;
    }
`;

const Card = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-top: 16px;
    background: #fff;
`;

const ToggleRow = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
`;

const ToggleInfo = styled.div`
    flex: 1;
`;

const SaveBar = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
`;

const SaveButton = styled.button`
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background: #318CD5;
    color: #fff;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const SupportPrivacy = () => {
    const { t } = useTranslation("supportPrivacy");
    const { sidebar } = useContext(SidebarContext);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [liveAllowed, setLiveAllowed] = useState(false);
    const [metricsEnabled, setMetricsEnabled] = useState(true);

    useEffect(() => {
        let alive = true;
        supportChatApi.get(`${base}/preferences`)
            .then(({ data }) => {
                if (!alive) return;
                setLiveAllowed(Boolean(data?.liveAllowed));
                setMetricsEnabled(data?.metricsEnabled !== false);
            })
            .catch(() => { if (alive) toast(t("loadError"), { autoClose: 3000, type: "error" }); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [t]);

    const save = async () => {
        setSaving(true);
        try {
            const body = { liveAllowed, metricsEnabled };
            // Backend exige confirmação explícita pra autorizar acompanhamento ao
            // vivo - esse clique em Salvar É a confirmação, não um efeito automático.
            if (liveAllowed) Object.assign(body, { confirmed: true, consentVersion: LIVE_CONSENT_VERSION });
            await supportChatApi.patch(`${base}/preferences`, body);
            toast(t("saved"), { autoClose: 2000, type: "success" });
        } catch (error) {
            toast(t("saveError"), { autoClose: 3000, type: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <PermissionGate permissions={["default"]}>
            <PageContainer $sidebar={sidebar}>
                <ContentWrapper>
                    <div className="page-title-with-subtitle">
                        <div className="page-title-with-icon">
                            <div className="page-icon"><MdShield /></div>
                            <h1>{t("page.title", "Privacidade do Suporte")}</h1>
                        </div>
                        <p>{t("page.subtitle", "Controle o que o suporte pode ver quando você pede ajuda.")}</p>
                    </div>

                    {loading ? (
                        <Card>{t("loading", "Carregando...")}</Card>
                    ) : (
                        <>
                            <Card>
                                <ToggleRow>
                                    <input
                                        type="checkbox"
                                        checked={liveAllowed}
                                        onChange={(e) => setLiveAllowed(e.target.checked)}
                                    />
                                    <ToggleInfo>
                                        <strong>{t("live.title", "Acompanhamento de tela ao vivo")}</strong>
                                        <p>{t("live.description", "Permite que um atendente veja sua tela em tempo real durante um atendimento que você mesmo inicia. Você vê um aviso sempre que isso estiver acontecendo e pode parar a qualquer momento, clicando em \"Parar acompanhamento\" no aviso.")}</p>
                                    </ToggleInfo>
                                </ToggleRow>
                            </Card>

                            <Card>
                                <ToggleRow>
                                    <input
                                        type="checkbox"
                                        checked={metricsEnabled}
                                        onChange={(e) => setMetricsEnabled(e.target.checked)}
                                    />
                                    <ToggleInfo>
                                        <strong>{t("metrics.title", "Métricas de uso")}</strong>
                                        <p>{t("metrics.description", "Envia dados agregados de uso (quais telas você usa, por quanto tempo) para ajudar o suporte a entender seu uso do sistema. Não inclui o conteúdo da tela.")}</p>
                                    </ToggleInfo>
                                </ToggleRow>
                            </Card>

                            <SaveBar>
                                <SaveButton type="button" onClick={save} disabled={saving}>
                                    {saving ? t("saving", "Salvando...") : t("save", "Salvar preferências")}
                                </SaveButton>
                            </SaveBar>
                        </>
                    )}
                </ContentWrapper>
            </PageContainer>
        </PermissionGate>
    );
};

export default SupportPrivacy;
